import { ImeasurementPoint, ImeasurementPointAnswer, ImeasurementPointResult } from '../models';

/*
 * Identifies the past result a draft was filled from, and how. Copy History is keyed separately from
 * the automatic fill so copying the same visit the draft was auto-filled from still copies everything.
 */
export const getHistorySeedKey = (lastResultID: string, isCopyingHistory: boolean): string =>
    `${isCopyingHistory ? 'copy' : 'history'}:${lastResultID}`;

interface IresolveFormAnswersArgs {
    temporary: boolean | undefined;
    answersSeededFrom: string | undefined;
    currentAnswers: ImeasurementPointAnswer[];
    lastResult: ImeasurementPointResult | undefined;
    measurementPointsByID: { [key: string]: ImeasurementPoint };
    isCopyingHistory: boolean;
}

/*
 * The answers the measurement point form seeds its controls from.
 *
 * A draft (temporary) result is filled from the last visit once: remembered points take the last
 * visit's answer, the rest keep the draft's own. After that the draft's answers ARE the engineer's -
 * every value change writes the whole form into redux, stamped with the returned seedKey - so a
 * control rebuild (In Use gate, Show All Tabs, Hide in Report) must read them as-is. Re-applying the
 * fill on a rebuild reverted remembered points to the last visit and dropped points the last visit
 * never answered back to their defaults.
 */
export const resolveFormAnswers = ({
    temporary,
    answersSeededFrom,
    currentAnswers,
    lastResult,
    measurementPointsByID,
    isCopyingHistory
}: IresolveFormAnswersArgs): { answers: ImeasurementPointAnswer[]; seedKey: string | undefined } => {
    if (temporary !== true || lastResult === undefined || lastResult.measurementPointAnswers === undefined) {
        return { answers: currentAnswers, seedKey: answersSeededFrom };
    }

    const seedKey = getHistorySeedKey(lastResult.id, isCopyingHistory);
    if (answersSeededFrom === seedKey) {
        return { answers: currentAnswers, seedKey };
    }

    const answers: ImeasurementPointAnswer[] = [];
    lastResult.measurementPointAnswers.forEach(answer => {
        const measurementPoint = measurementPointsByID[answer.measurementPointID];
        if (!measurementPoint) {
            return;
        }

        // Copy History copies every answer, even the ones not set to remember between inspections
        if (!isCopyingHistory && measurementPoint.selectRememberBetweenInspection === false) {
            const currentAnswer = currentAnswers.find(x => x.measurementPointID === answer.measurementPointID);
            if (currentAnswer) {
                answers.push(currentAnswer);
            } else {
                console.error('MP Answer not found');
            }
        } else {
            answers.push(answer);
        }
    });

    return { answers, seedKey };
};
